/**
 * Featured collections — stack mode:
 * - On load: scroll so the 2nd slide (b) is centered → it gets focus at 1.0, sides at 0.9.
 * - On scroll / resize: whichever slide is nearest horizontal center gets focus (c → d → e …).
 * - Extra tabs load via Section Rendering API (theme:section-rendering-replaced) or become visible
 *   (theme:tab-panel-visible) so each tab’s slider is bound and gets its own focus + scale.
 */
(function () {
  const FOCUS = 'fc-stack__item--focus';
  const ATTR = 'data-fc-stack';
  const BOUND = 'data-fc-stack-bound';

  function nearestCenter(slider) {
    const items = slider.querySelectorAll('.fc-stack__item');
    if (!items.length) return;
    const rect = slider.getBoundingClientRect();
    if (rect.width < 1) return;
    const centerX = rect.left + rect.width / 2;
    let best = null;
    let bestDist = Infinity;
    items.forEach((item) => {
      const r = item.getBoundingClientRect();
      if (r.width < 1) return;
      const itemCenter = r.left + r.width / 2;
      const d = Math.abs(itemCenter - centerX);
      if (d < bestDist) {
        bestDist = d;
        best = item;
      }
    });
    items.forEach((el) => el.classList.toggle(FOCUS, el === best));
  }

  /** First meaningful layout: center 2nd item (index 1) so b is active, not a. */
  function initialCenterSecond(slider, initialDoneRef) {
    if (initialDoneRef.done) return;
    if (slider.clientWidth < 1) return;
    const items = slider.querySelectorAll('.fc-stack__item');
    if (!items.length) return;
    initialDoneRef.done = true;
    if (items.length >= 2) {
      const target = items[1];
      const targetLeft = target.offsetLeft + target.clientWidth / 2 - slider.clientWidth / 2;
      const isRtl = typeof window.theme !== 'undefined' && window.theme.config && window.theme.config.rtl;
      const horizontalLeft = isRtl ? -Math.max(0, targetLeft) : Math.max(0, targetLeft);
      // Only adjust horizontal scroll. Avoid scrollIntoView so page won't jump to this section.
      slider.scrollTo({ left: horizontalLeft, behavior: 'instant' });
    }
    window.requestAnimationFrame(() => nearestCenter(slider));
  }

  function bind(slider) {
    if (slider.getAttribute(BOUND) === 'true') return;
    slider.setAttribute(BOUND, 'true');

    const initialDoneRef = { done: false };

    const run = () => {
      window.requestAnimationFrame(() => nearestCenter(slider));
    };

    slider.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', run);

    const ro = new ResizeObserver(() => {
      initialCenterSecond(slider, initialDoneRef);
      run();
    });
    ro.observe(slider);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            initialCenterSecond(slider, initialDoneRef);
            run();
          }
        });
      },
      { root: null, threshold: [0, 0.01] }
    );
    io.observe(slider);

    slider._fcStackCleanup = function () {
      slider.removeEventListener('scroll', run);
      window.removeEventListener('resize', run);
      ro.disconnect();
      io.disconnect();
      slider.removeAttribute(BOUND);
      slider.querySelectorAll('.' + FOCUS).forEach((el) => el.classList.remove(FOCUS));
      delete slider._fcStackCleanup;
      delete slider._fcStackOnPanelVisible;
    };

    slider._fcStackOnPanelVisible = function () {
      if (!initialDoneRef.done) {
        initialCenterSecond(slider, initialDoneRef);
      }
      nearestCenter(slider);
    };

    window.requestAnimationFrame(() => initialCenterSecond(slider, initialDoneRef));
    run();
  }

  function initAll(root) {
    const scope = root || document;
    scope.querySelectorAll('[' + ATTR + ']').forEach(bind);
  }

  /** New DOM under root (lazy tab) or tab panel just shown: bind new sliders + refresh focus. */
  function hydrateStackInRoot(root) {
    if (!root || !root.querySelectorAll) return;
    initAll(root);
    window.requestAnimationFrame(() => {
      root.querySelectorAll('[' + ATTR + ']').forEach((slider) => {
        if (typeof slider._fcStackOnPanelVisible === 'function') {
          slider._fcStackOnPanelVisible();
        }
      });
    });
  }

  function destroyIn(root) {
    const scope = root || document;
    scope.querySelectorAll('[' + ATTR + ']').forEach((el) => {
      if (typeof el._fcStackCleanup === 'function') el._fcStackCleanup();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector) initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (e.target && e.target.querySelector) destroyIn(e.target);
  });

  document.addEventListener('theme:section-rendering-replaced', (e) => {
    const root = e.detail && e.detail.root;
    if (root) hydrateStackInRoot(root);
  });

  document.addEventListener('theme:tab-panel-visible', (e) => {
    const panel = e.detail && e.detail.panel;
    if (panel) hydrateStackInRoot(panel);
  });
})();
